import type { ButtonOptions, OrgBranding, WeGivePayConfig } from './types'
import { openPopup } from './popup'
import type { WeGiveApi } from './api'

const DEFAULT_BRAND: OrgBranding = {
  uuid: 'wegive',
  name: 'WeGive',
  primaryColor: '#7f56d9',
  foregroundColor: '#ffffff',
}

interface MountedButton {
  unmount: () => void
}

/** Render the "Pay With WeGive" button. Renders immediately with sensible
 *  WeGive defaults, then upgrades to the org's brand (color + logo) as
 *  soon as `/api/pay/sessions` resolves. Embedders can override anything
 *  via `opts.brand` / `opts.logo` / `opts.className`. */
export function mountButton(
  config: WeGivePayConfig,
  api: WeGiveApi,
  target: string | HTMLElement,
  opts: ButtonOptions,
): MountedButton {
  if (!opts.label || !opts.label.trim()) {
    throw new Error('@wegive/pay: button `label` is required — set the CTA copy from your app')
  }

  const el = resolve(target)
  if (!el) throw new Error(`@wegive/pay: target element not found: ${String(target)}`)

  // Render synchronously with WeGive defaults so the embedder sees a usable
  // button on first paint — no FOUC, no spinner.
  const initialBranding: OrgBranding =
    api.bestKnownBranding ?? config.mockOrg ?? DEFAULT_BRAND

  const btn = renderButton(initialBranding, opts)
  const onClick = (e: Event) => {
    e.preventDefault()
    openPopup(config, opts).catch((err) => {
      opts.onError?.(err instanceof Error ? err : new Error(String(err)))
    })
  }
  btn.addEventListener('click', onClick)
  el.appendChild(btn)

  // Kick off the session fetch (cheap if cached) and upgrade styling when it lands.
  if (!api.cachedSession) {
    void api
      .sessionFor()
      .then((session) => applyBranding(btn, session.organization, opts))
      .catch(() => {
        // Silent fail — the default WeGive styling is already on screen.
      })
  }

  return {
    unmount() {
      btn.removeEventListener('click', onClick)
      btn.remove()
    },
  }
}

function renderButton(branding: OrgBranding, opts: ButtonOptions): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.setAttribute('data-wegive-pay', 'button')

  if (opts.className) {
    btn.className = opts.className
  } else {
    btn.className =
      'wg-pay-button inline-flex items-center justify-center gap-2 rounded-lg px-[18px] py-3 text-base font-semibold shadow-sm transition hover:opacity-95 active:scale-[0.99]'
    btn.style.fontFamily = opts.brand?.font ?? "'Inter', system-ui, -apple-system, sans-serif"
    btn.style.borderRadius = opts.brand?.radius ?? '8px'
    btn.style.border = '0'
    btn.style.cursor = 'pointer'
  }

  applyBranding(btn, branding, opts)
  return btn
}

function applyBranding(btn: HTMLButtonElement, branding: OrgBranding, opts: ButtonOptions) {
  const color = opts.brand?.color ?? branding.primaryColor ?? DEFAULT_BRAND.primaryColor!
  const foreground = opts.brand?.foreground ?? branding.foregroundColor ?? DEFAULT_BRAND.foregroundColor!
  const logo = opts.logo ?? branding.avatarUrl ?? branding.logoUrl ?? null
  const orgName = branding.name

  // Clear existing children
  while (btn.firstChild) btn.removeChild(btn.firstChild)

  if (!opts.className) {
    btn.style.backgroundColor = color
    btn.style.color = foreground
  }

  if (!opts.hideLogo) {
    if (logo) {
      const img = document.createElement('img')
      img.src = logo
      img.alt = orgName ?? ''
      img.style.height = '20px'
      img.style.width = 'auto'
      img.style.borderRadius = '4px'
      btn.appendChild(img)
    } else {
      // Generic mark — first letter on the foreground color.
      const dot = document.createElement('span')
      dot.textContent = (orgName?.[0] ?? 'W').toUpperCase()
      dot.style.display = 'inline-flex'
      dot.style.alignItems = 'center'
      dot.style.justifyContent = 'center'
      dot.style.height = '20px'
      dot.style.width = '20px'
      dot.style.borderRadius = '999px'
      dot.style.backgroundColor = 'rgba(255,255,255,0.2)'
      dot.style.fontSize = '11px'
      dot.style.fontWeight = '700'
      btn.appendChild(dot)
    }
  }

  const label = document.createElement('span')
  label.textContent = opts.label
  btn.appendChild(label)
}

function resolve(target: string | HTMLElement): HTMLElement | null {
  if (typeof target === 'string') return document.querySelector(target)
  return target
}
